import { useEffect, useMemo, useState } from 'react'
import BottomSheetContent from './BottomSheetContent'
import { EventNames } from '../../types/misc.types'
import { onReady, trackEvent } from '../../utils/appBridge'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import { roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import ComparisonBarGraph from '../graphs/ComparisonBarGraph'
import RenderHTML from '../ui-components/RenderHTML'
import TextBubble from '../ui-components/TextBubble'
import { fetchRegularVsDirectData } from '../../repository/MutualFundDetail'
import { useQuery } from '@tanstack/react-query'
import { RegularVsDirectDataResponseType } from '../../types/mutual-fund-detail.types'
import { LoadingSpinner } from '../LoadingSpinner'
import { RedirectionTab } from '../ui-components/RedirectionTab'

type BottomSheetWrapperPropTypes = {
  onDismiss: () => void
  ScreenName: string
}

const leftRectangle = `<svg xmlns="http://www.w3.org/2000/svg" width="3" height="52" viewBox="0 0 3 52" fill="none">
  <path d="M0 0C1.65685 0 3 1.34315 3 3V49C3 50.6569 1.65685 52 0 52V0Z" fill="#992617"/>
</svg>`

export const RegularPlanAlert = ({ onDismiss, ScreenName }: BottomSheetWrapperPropTypes) => {
  const heading = 'Regular Fund Alert'
  useEffect(() => {
    trackEvent({
      eventName: EventNames.KnowMoreBottomSheetLoad,
      payload: {
        eventId: heading,
        ScreenName
      }
    })
  }, [])
  return (
    <BottomSheetContent heading={heading} onDismiss={onDismiss}>
      <RegularPlanAlertContent />
    </BottomSheetContent>
  )
}

export const RegularPlanAlertContent = ({ isPage = false }: { isPage?: boolean }) => {
  const [regularVsDirectCommission, setRegularVsDirectCommission] = useState<
    RegularVsDirectDataResponseType['fundValues'][0] | null
  >(null)
  const { isin, data, directFundName } = useMetaDataContext()
  const directFundISIN = useMemo(() => {
    return data?.fundFamilyDirectFundIsin ?? ''
  }, [data])

  const {
    data: regularVsDirectData,
    isLoading,
    isFetching,
    isPending,
    isError
  } = useQuery<any, Error>({
    queryKey: ['regularVsDirectData', isin],
    queryFn: () => fetchRegularVsDirectData(isin),
    enabled: !!isin,
    retry: 1
  })

  useEffect(() => {
    if (regularVsDirectData) {
      const regularVsDirectDataCopy = { ...regularVsDirectData?.data } as RegularVsDirectDataResponseType
      setRegularVsDirectCommission(regularVsDirectDataCopy?.fundValues?.[0])
      onReady()
    }
  }, [regularVsDirectData])

  return (
    <div className='flex flex-col'>
      {regularVsDirectCommission?.diff?.value && regularVsDirectCommission?.diff?.value > 0 ? (
        <>
          <div className='text-[#6A6E6E] text-[13px] font-[400] leading-[150%]'>
            Mutual funds offer two plans: Regular & Direct. While both are the same fund, Regular funds charge a
            <span className='font-[700]'> higher expense ratio (TER) </span> that lowers your returns.
          </div>
          <div className='pt-4'>
            <div className='w-full h-[281px] flex flex-col border border-[#EBF2F2] bg-[#FBEFEF] rounded-lg mb-4'>
              <GraphBox
                isLoading={isLoading}
                isFetching={isFetching}
                isPending={isPending}
                isError={isError}
                regularVsDirectCommission={regularVsDirectCommission}
              />
            </div>
            <RedirectionTab isPage={isPage} directFundISIN={directFundISIN} directFundName={directFundName} />
            <div className='pt-6'>
              <TextBubble text="To avoid paying a higher expense ratio, look for funds which have ‘Direct’ in their name." />
            </div>
          </div>
        </>
      ) : (
        <>
          <GenericRegularFundAlert isPage={isPage} />
        </>
      )}
    </div>
  )
}

const GenericRegularFundAlert = ({ isPage = false }: { isPage?: boolean }) => {
  const { data, directFundName } = useMetaDataContext()
  const directFundISIN = useMemo(() => {
    return data?.fundFamilyDirectFundIsin ?? ''
  }, [data])

  return (
    <>
      <div className='text-[#6A6E6E] text-[13px] font-[400] leading-[150%]'>
        You're currently invested in a regular plan that includes third-party commissions, increasing its expense ratio.{' '}
        <br />
        <br />{' '}
        <span className='font-[500]'>
          {' '}
          Almost all funds have a direct plan alternative without the extra commissions
        </span>
        , so you keep more of the returns. <br />
        <br /> Unless your portfolio is managed by a mutual fund expert, consider switching to this fund's direct plan:
      </div>
      <div className='pt-4'>
        <RedirectionTab isPage={isPage} directFundISIN={directFundISIN} directFundName={directFundName} />
      </div>
    </>
  )
}

const GraphBox = ({
  isLoading,
  isFetching,
  isPending,
  regularVsDirectCommission,
  isError
}: {
  isLoading: boolean
  isFetching: boolean
  isPending: boolean
  regularVsDirectCommission: RegularVsDirectDataResponseType['fundValues'][0] | null
  isError: boolean
}) => {
  if (isLoading || isFetching || isPending) return <LoadingSpinner spinnerColor='#992617' />
  if (isError || !regularVsDirectCommission?.direct?.value)
    return (
      <div className='font-[400] flex w-full h-full justify-center text-center items-center text-[16px] text-[#992617]'>
        <div>Data Unavailable.</div>
      </div>
    )
  return (
    <>
      <div className='flex items-center gap-5 py-5'>
        <div>
          <RenderHTML innerHTMLText={leftRectangle} />
        </div>
        <div className='flex flex-col gap-[1px]'>
          <div className='text-[#992617] text-[14px] font-[700]'>
            {roundOffAndFormatNumberWithCurrency(regularVsDirectCommission?.diff?.value ?? 0)} paid due to high expense ratio
          </div>
          <div className='font-[400] text-[13px] text-[#992617]'>
            Regular funds returns are lower due to a higher <br /> expense ratio than Direct funds.
          </div>
        </div>
      </div>
      <div className='flex flex-1 h-1'>
        <ComparisonBarGraph
          regularValue={regularVsDirectCommission?.regular?.value ?? 0}
          directValue={regularVsDirectCommission?.direct?.value ?? 0}
        />
      </div>
    </>
  )
}
